import { useState, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import '../datepicker-custom.css'
import * as API from '../services/api'
import Toast from './Toast'

function InvoicesPage({ currentTIN, company = 'spn', isLoadingTIN = false }) {
  const [filters, setFilters] = useState({
    period: '',
    location_id: '',
    invoiceStart: '',
    invoiceEnd: '',
    startDate: null,
    endDate: null
  })
  const [invoices, setInvoices] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [toast, setToast] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [isBulkDownloading, setIsBulkDownloading] = useState(false)
  const [resultLimit, setResultLimit] = useState(100)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const companyName = company.toUpperCase()

  // Reset invoices list and selection when company changes
  useEffect(() => {
    setInvoices([])
    setCurrentPage(1)
    setSelectedIds(new Set())
  }, [company])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  // Convert Date object to YYYY-MM-DD for backend
  const convertToISODate = (date) => {
    if (!date) return ''
    try {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    } catch {
      return ''
    }
  }

  const handleSearch = async () => {
    setIsLoading(true)
    setSelectedIds(new Set())
    try {
      // Convert dates to ISO format for backend
      const searchFilters = {
        ...filters,
        company,
        limit: resultLimit,
        startDate: convertToISODate(filters.startDate),
        endDate: convertToISODate(filters.endDate)
      }
      const results = await API.listInvoices(searchFilters)
      setInvoices(results)
      setCurrentPage(1)
      setToast({ message: `Found ${results.length} invoices`, type: 'success' })
    } catch (error) {
      setToast({ message: `Search failed: ${error.message}`, type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadXML = async (invoiceId) => {
    try {
      await API.downloadXML(invoiceId, company)
      setToast({ message: 'XML downloaded successfully!', type: 'success' })
    } catch (error) {
      setToast({ message: `Download failed: ${error.message}`, type: 'error' })
    }
  }

  const handleBulkDownloadXML = async () => {
    if (selectedIds.size === 0) {
      setToast({ message: 'Please select at least one invoice', type: 'error' })
      return
    }

    setIsBulkDownloading(true)
    try {
      const invoiceIds = Array.from(selectedIds)
      await API.downloadBulkXML(invoiceIds, company)
      setToast({ message: `Bulk XML downloaded (${invoiceIds.length} invoices)`, type: 'success' })
    } catch (error) {
      setToast({ message: `Bulk download failed: ${error.message}`, type: 'error' })
    } finally {
      setIsBulkDownloading(false)
    }
  }

  // Checkbox handlers
  const handleSelectRow = (invoiceId) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(invoiceId)) {
        next.delete(invoiceId)
      } else {
        next.add(invoiceId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedIds.size === invoices.length) {
      // Deselect all
      setSelectedIds(new Set())
    } else {
      // Select all invoices (not just current page)
      const allIds = invoices.map(inv => inv._id || inv.invoice_id)
      setSelectedIds(new Set(allIds))
    }
  }

  const handleSelectPage = () => {
    const pageIds = currentInvoices.map(inv => inv._id || inv.invoice_id)
    const allPageSelected = pageIds.every(id => selectedIds.has(id))

    setSelectedIds(prev => {
      const next = new Set(prev)
      if (allPageSelected) {
        pageIds.forEach(id => next.delete(id))
      } else {
        pageIds.forEach(id => next.add(id))
      }
      return next
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const month = months[date.getMonth()]
      const year = date.getFullYear()
      return `${day}-${month}-${year}`
    } catch {
      return dateString
    }
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A'
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  // Pagination calculations
  const effectiveRowsPerPage = rowsPerPage === 'all' ? invoices.length : rowsPerPage
  const totalPages = effectiveRowsPerPage > 0 ? Math.ceil(invoices.length / effectiveRowsPerPage) : 1
  const startIndex = (currentPage - 1) * (effectiveRowsPerPage || invoices.length)
  const endIndex = startIndex + (effectiveRowsPerPage || invoices.length)
  const currentInvoices = invoices.slice(startIndex, endIndex)

  // Check if all items on current page are selected
  const isAllPageSelected = currentInvoices.length > 0 && currentInvoices.every(inv => selectedIds.has(inv._id || inv.invoice_id))
  const isAllSelected = invoices.length > 0 && selectedIds.size === invoices.length

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-orange-400 bg-clip-text text-transparent">
          Invoice Explorer ({companyName})
        </h2>
        <div className="text-sm text-gray-500">
          TIN: {isLoadingTIN ? (
            <span className="font-mono font-semibold text-gray-400 animate-pulse">Loading...</span>
          ) : (
            <span className="font-mono font-semibold text-gray-700">{currentTIN || 'N/A'}</span>
          )}
        </div>
      </div>
      
      {/* Filter Bar */}
      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
        {/* Compact container with max width */}
        <div className="max-w-4xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Period */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Period</label>
              <input 
                type="text" 
                value={filters.period}
                onChange={(e) => handleFilterChange('period', e.target.value)}
                placeholder="202602"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              />
            </div>
            
            {/* Location */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Location ID</label>
              <input 
                type="text" 
                value={filters.location_id}
                onChange={(e) => handleFilterChange('location_id', e.target.value)}
                placeholder="SPN.DPKAA20191000001"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              />
            </div>
            
            {/* Invoice Range (grouped, stays together) */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice Range</label>
              <div className="flex gap-4">
                <input 
                  type="text" 
                  value={filters.invoiceStart}
                  onChange={(e) => handleFilterChange('invoiceStart', e.target.value)}
                  placeholder="Start: 026361000"
                  className="flex-1 min-w-0 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                />
                <input 
                  type="text" 
                  value={filters.invoiceEnd}
                  onChange={(e) => handleFilterChange('invoiceEnd', e.target.value)}
                  placeholder="End: 026361999"
                  className="flex-1 min-w-0 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
            
            {/* Transaction Date Range (grouped, stays together) */}
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Transaction Date Range</label>
              <div className="flex gap-4">
                <div className="flex-1 min-w-0">
                  <DatePicker
                    selected={filters.startDate}
                    onChange={(date) => handleFilterChange('startDate', date)}
                    dateFormat="dd-MMM-yyyy"
                    placeholderText="Select start date"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    wrapperClassName="w-full"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <DatePicker
                    selected={filters.endDate}
                    onChange={(date) => handleFilterChange('endDate', date)}
                    dateFormat="dd-MMM-yyyy"
                    placeholderText="Select end date"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    minDate={filters.startDate}
                    wrapperClassName="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Result Limit */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Result Limit</label>
              <input 
                type="number" 
                value={resultLimit}
                onChange={(e) => {
                  const val = Math.min(Math.max(parseInt(e.target.value) || 1, 1), 2000)
                  setResultLimit(val)
                }}
                min="1"
                max="2000"
                placeholder="100"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              />
              <p className="text-xs text-gray-400 mt-1">Max: 2000</p>
            </div>
          </div>
        </div>
        
        <div className="mt-6 flex items-center gap-4">
          <button 
            onClick={handleSearch}
            disabled={isLoading}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Searching...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search Invoices
              </span>
            )}
          </button>

          {/* Select All + Bulk Download Buttons */}
          {invoices.length > 0 && (
            <>
              <button 
                onClick={handleSelectAll}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  {isAllSelected ? 'Deselect All' : `Select All (${invoices.length})`}
                </span>
              </button>

              <button 
                onClick={handleBulkDownloadXML}
                disabled={selectedIds.size === 0 || isBulkDownloading}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {isBulkDownloading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download XML ({selectedIds.size})
                  </span>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-orange-100">
          <h3 className="text-lg font-semibold text-gray-800">
            Results
            {invoices.length > 0 && (
              <span className="ml-3 text-sm font-normal text-gray-600">
                {invoices.length} total
                <span className="mx-2">&bull;</span>
                Showing {startIndex + 1}-{Math.min(endIndex, invoices.length)}
              </span>
            )}
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-12">
                  {invoices.length > 0 && (
                    <input
                      type="checkbox"
                      checked={isAllPageSelected}
                      onChange={handleSelectPage}
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                      title="Select/deselect current page"
                    />
                  )}
                </th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-24">XML</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Invoice</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Period</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Transaction Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Location</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Invoice Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-500 font-medium">Click "Search Invoices" to load data</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentInvoices.map((invoice, index) => {
                  const invoiceId = invoice._id || invoice.invoice_id
                  const isSelected = selectedIds.has(invoiceId)
                  return (
                    <tr key={index} className={`hover:bg-orange-50 transition-colors duration-150 ${isSelected ? 'bg-orange-50/50' : ''}`}>
                      <td className="px-4 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(invoiceId)}
                          className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        <button 
                          onClick={() => handleDownloadXML(invoiceId)}
                          className="inline-flex items-center justify-center p-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                          title="Download XML"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-900">
                        {invoice.invoice || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {invoice.period || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(invoice.transaction_date)}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        {invoice.location_id || 'N/A'}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        {invoice.location_name || 'N/A'}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-900 text-right font-semibold">
                        {formatCurrency(invoice.invoice_amount)}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {invoices.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600 font-medium">
                {rowsPerPage === 'all' 
                  ? `Showing all ${invoices.length} rows`
                  : `Page ${currentPage} of ${totalPages}`
                }
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">Rows per page:</label>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const val = e.target.value === 'all' ? 'all' : parseInt(e.target.value)
                    setRowsPerPage(val)
                    setCurrentPage(1)
                  }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value="all">ALL</option>
                </select>
              </div>
            </div>
            {rowsPerPage !== 'all' && totalPages > 1 && (
              <div className="flex gap-2">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  First
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  Previous
                </button>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  Next
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  Last
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default InvoicesPage
